/**
 * Blocks the storefront when maintenance mode is enabled in store settings.
 * POS routes are always allowed through.
 */
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Wrench } from 'lucide-react'
import { useFeatures } from '@/hooks/useFeatures'
import logoImg from '@/assets/logo.png'

interface MaintenanceGateProps {
  children: ReactNode
}

function MaintenanceScreen() {
  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 via-white to-primary-50/30 px-6"
      role="status"
      aria-live="polite"
    >
      <div className="flex max-w-md flex-col items-center gap-6 text-center">
        <img
          src={logoImg}
          alt="Arts and Handicrafts"
          className="h-20 w-auto object-contain drop-shadow-sm sm:h-24"
        />
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <Wrench className="h-7 w-7" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h1 className="font-heading text-2xl font-bold text-gray-900">We&apos;ll be back soon</h1>
          <p className="text-sm text-gray-600">
            Our store is undergoing scheduled maintenance. Please check back shortly.
          </p>
        </div>
      </div>
    </div>
  )
}

export function MaintenanceGate({ children }: MaintenanceGateProps) {
  const { pathname } = useLocation()
  const { features, isLoading } = useFeatures()

  if (pathname.startsWith('/pos')) {
    return <>{children}</>
  }

  if (isLoading) {
    return <>{children}</>
  }

  if (features.maintenance_mode) {
    return <MaintenanceScreen />
  }

  return <>{children}</>
}
